import { BiChatContextType, BrandInsightChatContexts, ChatContextType } from '../enums/chat.js';
import {
  type BrandInsightsContext,
  type BrandInsightsInstanceContext,
  type BrandInsightsPageContext,
} from '../types/chat.context.js';

export function isEverythingContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Global) return true;

  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Global)
    return true;

  return false;
}

export function isProjectContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Project) return true;

  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Project)
    return true;

  return false;
}

export function isFolderContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Folder)
    return true;
  if (context.type === ChatContextType.Folder) return true;

  return false;
}

export function isBrandContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Brand)
    return true;
  if (context.type === ChatContextType.Brand) return true;

  return false;
}

export function isTranscriptContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Transcript)
    return true;
  if (context.type === ChatContextType.Transcript) return true;
  return false;
}

export function isDocumentContext(context: BrandInsightsPageContext.Item): boolean {
  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Document)
    return true;
  if (context.type === ChatContextType.Document) return true;
  return false;
}

export function isBrandInsightsContext(context: BrandInsightsPageContext.Item): boolean {
  return context.type === ChatContextType.Agent && BrandInsightChatContexts.includes(context.data.identifier);
}
export function isBrandInsightsSubjectScopedContext(context: BrandInsightsPageContext.Item): boolean {
  return resolveSubjectScopedKind(context) !== null;
}

export type SubjectScopedKind = 'project' | 'document' | 'folder' | 'brand' | 'transcript';

/**
 * Which single-entity "subject" kind this context is scoped by (Project/Document/Folder/Brand/
 * Transcript, direct or Agent-wrapped), or `null` if none. Single source of truth for
 * `isSubjectScopedContext`, the side panel's `resolveMode`, and `getFilterLabelForContext`.
 */
export function resolveSubjectScopedKind(context: BrandInsightsPageContext.Item): SubjectScopedKind | null {
  if (isProjectContext(context)) return 'project';
  if (isDocumentContext(context)) return 'document';
  if (isFolderContext(context)) return 'folder';
  if (isBrandContext(context)) return 'brand';
  if (isTranscriptContext(context)) return 'transcript';
  return null;
}

/**
 * Chat context metadata is carried by some context variants (`*WithMetadata`) but not others —
 * `'metadata' in context` narrows to whichever the given instance actually is at runtime.
 */
export function getChatContextMetadata(
  context: BrandInsightsInstanceContext.Item
): Record<string, unknown> | undefined {
  return 'metadata' in context ? (context.metadata as Record<string, unknown> | undefined) : undefined;
}

export function getSegmentFromChatContext(
  context: BrandInsightsInstanceContext.Item
): BrandInsightsContext.Project.Segment | undefined {
  const metadata = getChatContextMetadata(context);
  const segment = metadata?.segment;
  return segment && typeof segment === 'object' && 'id' in segment && 'name' in segment
    ? (segment as BrandInsightsContext.Project.Segment)
    : undefined;
}

export function getSegmentIdentifierFromChatContext(
  context: BrandInsightsInstanceContext.Item
): string | undefined {
  return getSegmentFromChatContext(context)?.id;
}

/**
 * Coerces an Agent context's `subject.id` (`number | string`) to a numeric entity id —
 * a bare `as number` assertion does nothing at runtime. Returns `undefined` (not `NaN`)
 * for a missing/non-numeric id so callers' existing `!id` fallbacks still apply.
 */
export function resolveSubjectId(subject: { id: number | string } | null | undefined): number | undefined {
  if (subject == null) {
    return undefined;
  }

  const id = typeof subject.id === 'number' ? subject.id : Number(subject.id);
  return Number.isFinite(id) ? id : undefined;
}

export function extractBrandInsightsIds(context: BrandInsightsPageContext.Item) {
  let brandId: number | undefined = undefined;
  let folderId: number | undefined = undefined;
  let projectId: number | undefined = undefined;
  const fileId: number | undefined = undefined;
  const transcriptId: number | undefined = undefined;

  if (context.type === ChatContextType.Brand) {
    brandId = context.data.brandId;
  }
  if (context.type === ChatContextType.Folder) {
    folderId = context.data.folderId;
  }
  if (context.type === ChatContextType.Project) {
    projectId = context.data.projectId;
  }
  if (context.type === ChatContextType.Agent) {
    if (context.data.identifier === BiChatContextType.Brand) {
      brandId = resolveSubjectId(context.data.subject);
    }
    if (context.data.identifier === BiChatContextType.Folder) {
      folderId = resolveSubjectId(context.data.subject);
    }
    if (context.data.identifier === BiChatContextType.Project) {
      projectId = resolveSubjectId(context.data.subject);
    }
  }

  return {
    brandId,
    folderId,
    projectId,
    fileId,
    transcriptId,
  };
}
